import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSplitDefined } from '@/api/getSplitDetails';
import { BUTTON_SOURCES } from '@/constants/buttonSources';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useSplitDetails } from '@/hooks/useSplitDetails';
import { useTipConfig } from '@/hooks/useTipConfig';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import CheckoutBottomSheetCustomTip from '@/screens/Checkout/CheckoutBottomSheetCustomTip';
import { sendBusinessEvent, setTipPercentage } from '@/screens/Checkout/monitoring';
import { calculateTipToPay } from '@/utils/calculateTipToPay';
import { trackButtonClick } from '@/utils/gtmButtonClicked';
import { calculateFinalTipAmount } from '@/utils/tipCalculations';
import TipChip from './components/TipChip/TipChip';
import styles from './Tips.module.scss';

interface Props {
  orderNumber: string;
  orderId?: string;
}

const tipOptions = [
  { tipPercentage: 0 },
  { tipPercentage: 7 },
  { tipPercentage: 10 },
  { tipPercentage: 0, isCustom: true },
];

function findDefaultIndex(defaultPercentage: number): number {
  const idx = tipOptions.findIndex(o => !o.isCustom && o.tipPercentage === defaultPercentage);
  return idx >= 0 ? idx : 0;
}

export const Tips = memo(({ orderNumber, orderId }: Props) => {
  const { t } = useTranslation();
  const { defaultTipPercentage, mostCommonTipPercentage } = useTipConfig();
  const defaultIndex = useMemo(
    () => findDefaultIndex(defaultTipPercentage),
    [defaultTipPercentage]
  );
  const [selectedTipIndex, setSelectedTipIndex] = useState(defaultIndex);
  const {
    state,
    actions: { changeTip, toggleCustomTip, setCustomTip, resetTipToDefault },
  } = useCheckoutContext();
  const { isProcessing, baseToPay, isCustomTipOpen, customTipAmount } = state;
  const { data: splitDetails } = useSplitDetails({ orderNumber });
  const { queryParams } = useValidateQueryParams();
  const { brandId, tableNumber, counterNumber } = queryParams();
  const browserUserId = useBrowserUserId();

  const yourShare = useMemo(
    () =>
      isSplitDefined(splitDetails) && splitDetails.type !== 'SPLIT_BY_ITEMS'
        ? splitDetails.onePersonSplit
        : baseToPay,
    [splitDetails, baseToPay]
  );

  const handleTipClick = useCallback(
    (index: number) => {
      if (isProcessing) return;
      const isCustom = !!tipOptions[index].isCustom;
      const tipPercentage = tipOptions[index].tipPercentage;

      // For non-custom tips, send event immediately and store tipPercentage for payment events
      if (!isCustom) {
        const tipAmount = calculateTipToPay(yourShare, tipPercentage);
        sendBusinessEvent('kitopay.tip.selected', {
          tipPercentage,
          tipAmount,
          orderValue: yourShare,
          isCustom: false,
        });
        setTipPercentage(tipPercentage);
      }
      // For custom tips, event is sent in onConfirm when user enters amount

      trackButtonClick({
        buttonId: isCustom ? 'tip-custom' : `tip-${tipOptions[index].tipPercentage}`,
        buttonName: isCustom ? 'Custom tip' : `${tipOptions[index].tipPercentage}% tip`,
        buttonSource: BUTTON_SOURCES.MAIN_SCREEN,
        urlParams: {
          brandId: brandId || undefined,
          orderId: orderId || undefined,
          tableNumber,
          counterNumber,
        },
        extraParams: {
          browser_user_id: browserUserId,
        },
      });
      setSelectedTipIndex(index);
      if (isCustom) {
        toggleCustomTip();
      } else {
        const amount = calculateTipToPay(yourShare, tipOptions[index].tipPercentage);
        changeTip(amount, tipOptions[index].tipPercentage);
      }
    },
    [
      isProcessing,
      changeTip,
      toggleCustomTip,
      yourShare,
      brandId,
      tableNumber,
      counterNumber,
      orderId,
      browserUserId,
    ]
  );

  // Reset tip to default when closing custom tip bottomsheet
  useEffect(() => {
    if (!isCustomTipOpen && tipOptions[selectedTipIndex].isCustom && customTipAmount === null) {
      setSelectedTipIndex(defaultIndex);
      resetTipToDefault();
    }
  }, [isCustomTipOpen, selectedTipIndex, customTipAmount, resetTipToDefault, defaultIndex]);

  // set tip when baseToPay changes or selectedTipIndex changes (but not when customTipAmount changes)
  useEffect(() => {
    if (baseToPay && !(tipOptions[selectedTipIndex].isCustom && customTipAmount !== null)) {
      const amount = calculateTipToPay(yourShare, tipOptions[selectedTipIndex].tipPercentage);
      changeTip(amount, tipOptions[selectedTipIndex].tipPercentage);
    }
  }, [baseToPay, selectedTipIndex, yourShare, changeTip, customTipAmount]);

  // set selected to custom when customTipAmount changes
  useEffect(() => {
    if (customTipAmount !== null) {
      setSelectedTipIndex(tipOptions.length - 1); // custom is last index
    }
  }, [customTipAmount]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 data-testid="tip-your-server-header">{t('checkout.tips.tipServer')}</h2>
        <h3 data-testid="tip-your-server-subheader">{t('checkout.tips.tipGoesToServer')}</h3>
      </div>
      <div className={styles.tipsWrapper}>
        <div className={styles.tips}>
          {tipOptions.map(({ tipPercentage, isCustom }, index) => (
            <TipChip
              key={isCustom ? 'custom' : tipPercentage}
              amount={
                isCustom
                  ? calculateFinalTipAmount(customTipAmount, yourShare, tipPercentage)
                  : calculateTipToPay(yourShare, tipPercentage)
              }
              percentage={tipPercentage}
              disabled={isProcessing}
              onClick={() => handleTipClick(index)}
              selected={index === selectedTipIndex}
              isCustom={isCustom}
              mostCommonPercentage={mostCommonTipPercentage}
            />
          ))}
        </div>
      </div>
      {isCustomTipOpen && (
        <CheckoutBottomSheetCustomTip
          onConfirm={amount => {
            // Send event with custom tip amount
            sendBusinessEvent('kitopay.tip.selected', {
              tipPercentage: 'custom',
              tipAmount: amount,
              orderValue: yourShare,
              isCustom: true,
            });
            // Store tipPercentage for payment events (tipAmount comes from MoneyHash)
            setTipPercentage('custom');
            setCustomTip(amount);
          }}
          onClose={() => {
            resetTipToDefault();
          }}
        />
      )}
    </div>
  );
});
